import rawDataset from '../../../data/fragrance_top500_v0.3.json';
import type { Confidence, FragranceDataset, FragranceRecord, RetailLink } from './types';

export const fragranceDataset = rawDataset as FragranceDataset;
export const fragrances = fragranceDataset.fragrances;

const normalise = (value: string) => value.toLocaleLowerCase().trim().replace(/[\s-]+/g, '_');
const addIndex = (map: Map<string, FragranceRecord[]>, key: string | null, record: FragranceRecord) => {
  if (!key) return;
  const normalised = normalise(key);
  map.set(normalised, [...(map.get(normalised) ?? []), record]);
};

const byId = new Map(fragrances.map((item) => [item.fragrance_id, item]));
const byBrand = new Map<string, FragranceRecord[]>();
const byName = new Map<string, FragranceRecord[]>();
const byFamily = new Map<string, FragranceRecord[]>();
const byAccord = new Map<string, FragranceRecord[]>();
const byNote = new Map<string, FragranceRecord[]>();
const byConcentration = new Map<string, FragranceRecord[]>();
const byMarketedGender = new Map<string, FragranceRecord[]>();

for (const fragrance of fragrances) {
  addIndex(byBrand, fragrance.identity.brand, fragrance);
  addIndex(byName, fragrance.identity.name, fragrance);
  addIndex(byFamily, fragrance.official_classification.family, fragrance);
  addIndex(byConcentration, fragrance.identity.concentration, fragrance);
  addIndex(byMarketedGender, fragrance.identity.marketed_gender, fragrance);
  fragrance.official_classification.accords.forEach((value) => addIndex(byAccord, value, fragrance));
  fragrance.normalised_notes.forEach((value) => addIndex(byNote, value.canonical_name, fragrance));
}

export const catalogueIndexes = {
  byId,
  byBrand,
  byName,
  byFamily,
  byAccord,
  byNote,
  byConcentration,
  byMarketedGender,
};

export const getFragrance = (id: string) => byId.get(id);

export const getAllFeatureKeys = (record: FragranceRecord) =>
  new Set(
    [
      record.official_classification.family,
      ...record.official_classification.accords,
      ...record.normalised_notes.map((note) => note.canonical_name),
      ...record.normalised_notes.map((note) => note.note_family),
      ...record.descriptive_tags.sensory,
    ]
      .filter((value): value is string => Boolean(value))
      .map(normalise),
  );

export const filterByExclusions = (
  records: FragranceRecord[],
  excludedIds: string[],
  dislikedNotes: string[],
) => {
  const ids = new Set(excludedIds);
  const notes = new Set(dislikedNotes.map(normalise));
  return records.filter(
    (record) =>
      !ids.has(record.fragrance_id) &&
      !record.constraints.prominent_notes.some((note) => notes.has(normalise(note))),
  );
};

export const findNoteMatches = (note: string) => byNote.get(normalise(note)) ?? [];
export const findAccordMatches = (accord: string) => byAccord.get(normalise(accord)) ?? [];

export const assessCompleteness = (record: FragranceRecord) => {
  const checks = [
    record.official_classification.accords.length > 0,
    record.normalised_notes.length > 0,
    Boolean(record.official_classification.family),
    Boolean(record.identity.release_year),
    Boolean(record.identity.concentration),
    record.identity.perfumers.length > 0,
  ];
  return checks.filter(Boolean).length / checks.length;
};

export const verifiedRetailerLinks = (record: FragranceRecord): RetailLink[] =>
  record.availability.retailers
    .filter((retailer) => retailer.listed && retailer.last_checked)
    .map((retailer) => ({
      fragranceId: record.fragrance_id,
      retailer: retailer.name,
      market: 'unspecified',
      url: retailer.product_url,
      affiliate: false,
      verifiedAt: retailer.last_checked!,
    }));

export const purchaseSearchQuery = (record: FragranceRecord) =>
  `${record.identity.name} by ${record.identity.brand}`;

export const purchaseSearchUrl = (record: FragranceRecord) =>
  `https://www.google.com/search?q=${encodeURIComponent(purchaseSearchQuery(record))}`;

export const safeValue = (value: string | number | null | undefined, fallback = 'Not verified') =>
  value === null || value === undefined || value === '' ? fallback : String(value);

export const confidenceLabel = (confidence: Confidence) =>
  ({ high: 'Higher source confidence', medium: 'Moderate source confidence', low: 'Limited source confidence', unknown: 'Source confidence unknown' })[confidence];

// Current price and availability are intentionally unverified in v0.3. Unknown values never pass as verified.
export const filterByVerifiedPrice = (records: FragranceRecord[]) => records.filter(() => false);
